import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient, unwrap } from '@/shared/api-client/workspace';

/**
 * A customer's Standard Pass visits as Operations › Customers reads them (ADR 0072 §8): each visit's
 * Pass and variant, guests and frozen window. `undo` is null while the visit holds its seats; once a
 * manager undoes it, it carries when, why and who.
 */
export interface StandardPassVisit {
  id: string;
  saleId: string;
  passName: string;
  variantName: string;
  guests: number;
  hours: number;
  acceptedAt: string;
  windowStartsAt: string;
  windowEndsAt: string;
  undo: {
    undoneAt: string;
    reason: string;
    undoneBy: string | null;
  } | null;
}

const visitsPath =
  '/api/v1/venue-workspace/venues/{venueId}/customers/{customerId}/standard-pass-visits';
const undoPath =
  '/api/v1/venue-workspace/venues/{venueId}/customers/{customerId}/standard-pass-visits/{visitId}/undo';

const getStandardPassVisits = (venueId: string, customerId: string): Promise<StandardPassVisit[]> =>
  unwrap(apiClient.GET(visitsPath, { params: { path: { venueId, customerId } } })) as Promise<
    StandardPassVisit[]
  >;

const undoStandardPassVisit = (
  venueId: string,
  customerId: string,
  visitId: string,
  reason: string,
) =>
  unwrap(
    apiClient.POST(undoPath, {
      params: { path: { venueId, customerId, visitId } },
      body: { reason },
    }),
  );

const visitsKey = (venueId: string, customerId: string) =>
  ['standard-pass-visits', venueId, customerId] as const;

export function useStandardPassVisits(venueId: string, customerId: string, enabled: boolean) {
  return useQuery({
    queryKey: visitsKey(venueId, customerId),
    queryFn: () => getStandardPassVisits(venueId, customerId),
    enabled,
  });
}

export function useUndoStandardPassVisit(venueId: string, customerId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ visitId, reason }: { visitId: string; reason: string }) =>
      undoStandardPassVisit(venueId, customerId, visitId, reason),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: visitsKey(venueId, customerId) }),
  });
}
